import { getNthColor } from "@common/helpers/colors";
import type {
    AvatarProps,
    AvatarShape,
    AvatarSize,
    AvatarTone,
    AvatarVariant,
} from "@design-system/Avatar/Avatar";
import type { AvatarGroupSize } from "@design-system/Avatar/AvatarGroup";
import conradPhoto from "@static/images/testimonials/conrad.jpeg";
import mercuryLogo from "@icons/logos/Mercury.svg";
import paypalLogo from "@icons/logos/PaypalBlue.svg";
import plaidLogo from "@icons/logos/Plaid.svg";
import rampLogo from "@icons/logos/Ramp.svg";
import stripeLogo from "@icons/logos/StripeRound.svg";

export const avatarSizes = [
    "xs",
    "sm",
    "md",
    "lg",
] as const satisfies readonly AvatarSize[];

export const avatarShapes = [
    "circle",
    "square",
    "diamond",
] as const satisfies readonly AvatarShape[];

export const avatarVariants = [
    "solid",
    "outline",
] as const satisfies readonly AvatarVariant[];

export const avatarTones = [
    "colorful",
    "neutral",
] as const satisfies readonly AvatarTone[];

export const avatarGroupSizes = [
    "xs",
    "sm",
    "md",
] as const satisfies readonly AvatarGroupSize[];

/** Neutral avatars take no colour, so the colour is only passed to colorful ones. */
export const avatarPaintProps = ({
    variant,
    tone,
    color,
}: {
    variant: AvatarVariant;
    tone: AvatarTone;
    color: string | undefined;
}) => (tone === "neutral" ? { variant, tone } : { variant, tone, color });

/** The group size matching an avatar size; groups have no `lg`. */
export const toAvatarGroupSize = (
    size: AvatarSize,
): AvatarGroupSize | undefined => (size === "lg" ? undefined : size);

export const avatarSizePixels: Record<AvatarSize, number> = {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32,
};

export const avatarContents = [
    "photo",
    "initialColor",
    "initial",
    "brokenPhoto",
    "fallback",
] as const;

export type AvatarContent = (typeof avatarContents)[number];

export const avatarContentLabels: Record<AvatarContent, string> = {
    photo: "photo",
    initialColor: "initial + color",
    initial: "initial",
    brokenPhoto: "broken photo",
    fallback: "fallback slot",
};

export const pastelColors = [
    { id: "peach", value: "#FADEC4" },
    { id: "mint", value: "#BDF0D5" },
    { id: "lilac", value: "#D9CCFF" },
] as const;

export type PastelColorId = (typeof pastelColors)[number]["id"];

export const pastelColorValue = (id: PastelColorId | null) =>
    pastelColors.find((color) => color.id === id)?.value;

export const personPhotoSrc = conradPhoto;

export const brokenPhotoSrc = "data:image/png;base64,broken";

export const firmLogoSrc = `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><rect width="24" height="24" fill="#1F3A5F"/><path d="M7 7h10v2.2L10.4 15H17v2H7v-2.2L13.6 9H7z" fill="#fff"/></svg>',
)}`;

/** One stack member: an `Avatar`'s props plus a key. */
export type PlaygroundPerson = AvatarProps & { id: string };

export const playgroundPeople: readonly PlaygroundPerson[] = [
    { id: "thea", name: "Thea Morgan", src: "/images/thea.png" },
    { id: "mark", name: "Mark Osei", color: "#FADEC4" },
    { id: "echo", name: "Echo Lin", src: "/images/echo.png" },
    { id: "david", name: "David Cohen", color: "#BDF0D5" },
    { id: "anna", name: "Anna Lee", color: "#D9CCFF" },
    { id: "conrad", name: "Conrad Wolff", src: conradPhoto },
];

/** Entity samples with backend-style `iconColor`s from `getNthColor`. */
export const playgroundEntities: readonly PlaygroundPerson[] = [
    "Internet Money",
    "Process Squad",
    "AZ Rentals",
    "Northwind Holdings",
    "Cedar & Co",
    "Harbor Labs",
].map((name, index): PlaygroundPerson => ({
    id: `entity-${index}`,
    name,
    shape: "square",
    color: getNthColor(index),
}));

export const playgroundClasses: readonly PlaygroundPerson[] = [
    "Marketing",
    "Operations",
    "Engineering",
    "Sales",
    "Research",
    "Admin",
].map((name, index): PlaygroundPerson => ({
    id: `class-${index}`,
    name,
    shape: "diamond",
    color: getNthColor(index + 6),
}));

/** People, a firm, entities and a consolidation group in one stack. */
export const mixedStackPeople: readonly PlaygroundPerson[] = [
    { id: "thea", name: "Thea Morgan", src: "/images/thea.png" },
    {
        id: "zhong",
        name: "Zhong & Sanches",
        src: firmLogoSrc,
        shape: "square",
    },
    { id: "mark", name: "Mark Osei", color: "#FADEC4" },
    {
        id: "internet-money",
        name: "Internet Money",
        color: getNthColor(0),
        shape: "square",
    },
    {
        id: "consolidated",
        name: "Consolidated",
        shape: "square",
        variant: "outline",
        tone: "neutral",
    },
    { id: "anna", name: "Anna Lee", color: "#D9CCFF" },
];

/** The billing plan card's integration logos, shown with `extraCount={50}`. */
export const playgroundIntegrations: readonly PlaygroundPerson[] = [
    { id: "stripe", name: "Stripe", src: stripeLogo },
    { id: "paypal", name: "PayPal", src: paypalLogo },
    { id: "mercury", name: "Mercury", src: mercuryLogo },
    { id: "ramp", name: "Ramp", src: rampLogo },
    { id: "plaid", name: "Plaid", src: plaidLogo },
];

export const groupCounts = [
    { label: "1", count: 1 },
    { label: "2", count: 2 },
    { label: "3", count: 3 },
    { label: "4", count: 4 },
    { label: "4+", count: 6 },
] as const;

/** The sample stack matching an avatar shape, for the sandbox stage: people are circles, entities squares, classes diamonds. */
export const stackForShape = (
    shape: AvatarShape,
): readonly PlaygroundPerson[] => {
    switch (shape) {
        case "circle":
            return playgroundPeople;
        case "square":
            return playgroundEntities;
        case "diamond":
            return playgroundClasses;
    }
};
